import { css } from 'styled-components';
import { HeColours } from './HeColours';
import { ThemeColours } from './ThemeColours';

export const DESKTOP_HEADER_HEIGHT = '104px';

export const MOBILE_HEADER_HEIGHT = '80px';

export const TRANSITION_LENGTH = 200;

export const STANDARD_SHRINK = 0.92;

export const STANDARD_DOUBLE_SHRINK = STANDARD_SHRINK * STANDARD_SHRINK;

export const STANDARD_GROW = 1 / STANDARD_SHRINK;

export const FONT_NAME = 'MessinaSansWeb';

export const FONT_LIST = `${FONT_NAME}, 'Arial', sans-serif`;

export const FONT_STACK = css`
  font-family: ${FONT_LIST};
`;

export const SHADOW = {
  SHALLOW: `0 -6px 20px ${HeColours.gray[100]}`,
  /* The LARGE value is ThemeColours.mono900 with an opacity value added */
  LARGE: `0px 1px 10px rgba(7, 35, 38, 0.1)`,
};

export const BOX_SIZING_BORDER_BOX = css`
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
`;

export enum FontWeight {
  LIGHT = 300,
  REGULAR = 400,
  SEMI_BOLD = 600,
  BOLD = 700,
  HEADING_1 = 600,
  HEADING_2 = 600,
  HEADING_3 = 600,
  HEADING_4 = 600,
  HEADING_5 = 600,
  SUB_HEADING = 400,
}

export enum FontSize {
  EXTRA_EXTRA_SMALL = '10px',
  EXTRA_SMALL = '12px',
  SMALL = '14px',
  REGULAR = '16px',
  LARGE = '20px',
  HEADING_1 = '56px',
  HEADING_1_SMALL = '48px',
  HEADING_2 = '48px',
  HEADING_2_SMALL = '32px',
  HEADING_3 = '32px',
  HEADING_3_SMALL = '24px',
  HEADING_4 = '24px',
  HEADING_4_SMALL = '16px',
  HEADING_5 = '18px',
  HEADING_5_SMALL = '16px',
  SUB_HEADING = '24px',
  BODY_REGULAR = '16px',
}

export enum LineHeight {
  EXTRA_SMALL = '16px',
  SMALL = '20px',
  REGULAR = '24px',
  LARGE = '30px',
  HEADING_1 = '64px',
  HEADING_1_SMALL = '56px',
  HEADING_2 = '56px',
  HEADING_2_SMALL = '40px',
  HEADING_3 = '40px',
  HEADING_3_SMALL = '32px',
  HEADING_4 = '32px',
  HEADING_4_SMALL = '24px',
  HEADING_5 = '24px',
  HEADING_5_SMALL = '24px',
  HEADING_6 = '28px',
  HEADING_6_SMALL = '28px',
  SUB_HEADING = '32px',
  BODY_REGULAR = '24px',
}

export enum BorderRadius {
  NONE = '0',
  SMALL = '4px',
  MEDIUM = '8px',
  LARGE = '16px',
}

export const LINK_STYLE = css`
  ${FONT_STACK};
  color: ${ThemeColours.primary};
  cursor: pointer;
  font-weight: ${FontWeight.SEMI_BOLD};
  text-decoration: none;
`;

export const STANDARD_TRANSITION = css`
  transition-duration: ${TRANSITION_LENGTH}ms;
  transition-property: filter, transform, color, background-color, box-shadow, border-color, opacity;
`;

export const RESET_IOS_INPUT_STYLE = css`
  -webkit-appearance: none;
`;

export const PRESSABLE_LINK = css`
  ${STANDARD_TRANSITION};
  cursor: pointer;
  user-select: none;

  &:focus {
    box-shadow: 0 0 0 2px ${ThemeColours.primary700};
    outline: none;
  }

  &:hover {
    filter: brightness(${STANDARD_SHRINK});
  }

  &:active {
    filter: brightness(${STANDARD_DOUBLE_SHRINK});
  }
`;

export const PRESSABLE = css`
  ${PRESSABLE_LINK};
  &:active {
    transform: scale(${STANDARD_SHRINK});
  }
`;

export enum ZIndex {
  /** Used for non-interactive content that sits under regular content */
  BEHIND = -1,
  /** Used to create a stacking context */
  NORMAL = 0,
  /** Used for non-interactive content partially covering regular content */
  TOOLTIP = 1,
  /** Used for interactive content that sits under interactive elements */
  UNDERLAY = 2,
  /** Used for interactive content partially covering regular content */
  OVERLAY = 3,
  /** It is like overlay, but higher priority. Used on search filter overlay */
  SEARCH_OVERLAY = 4,
  /** Used for interactive content completely covering regular content */
  POPUP = 5,
  /** Used for baseweb popover elements, which are unaware of the ZIndex hierarchy used on the rest
   * of the page
   */
  BASEWEB = 6,
}

/** @see https://tailwindcss.com/docs/breakpoints */
export enum Breakpoint {
  SMALL = '@media (min-width: 640px)',
  MEDIUM = '@media (min-width: 768px)',
  LARGE = '@media (min-width: 1024px)',
  HEIGHT_LARGE = '@media (min-height: 1024px)',
  EXTRA_LARGE = '@media (min-width: 1280px)',
  EXTRA_EXTRA_LARGE = '@media (min-width: 1536px)',
}

export enum BreakpointHeight {
  MEDIUM = '@media (min-height: 768px)',
}

export enum BreakpointIdentifier {
  SM = 'Small',
  MD = 'Medium',
  LG = 'Large',
  XL = 'XLarge',
  XXL = 'XXLarge',
}

export enum ScreenSize {
  SMALL = 640,
  MEDIUM = 768,
  LARGE = 1024,
  EXTRA_LARGE = 1280,
  EXTRA_EXTRA_LARGE = 1536,
}

export enum Orientation {
  LANDSCAPE = '@media (orientation: landscape)',
  PORTRAIT = '@media (orientation: portrait)',
}
